/**
 * PATH: packages/email-hub/src/feature/components/PingTeammateButton.tsx
 * FILE_ID: EMAIL-COLLAB-COMPONENT-001
 * PURPOSE: Always-visible header icon that lets the operator ping a teammate
 *          (estimators-first) about THIS email and flag it URGENT. Picking a
 *          teammate hands the email off to them — server-side this grants
 *          visibility AND fires an email + in-app notification.
 * WHY:     Operator (2026-06-03): "I want to ping jake in this email as he is
 *          estimating and raise it as an urgent notification — why is there no
 *          small icon to team ping?" The handoff engine already existed
 *          (EstimatorAllocator) but was GATED behind AI `quote_request`
 *          classification, so on an "Uncategorised" RFQ no ping affordance
 *          showed. This surfaces the same backend as a one-click header icon on
 *          EVERY email, with an URGENT framing carried in the notification text.
 * FLOW:
 *   ┌──────────────┐   ┌─────────────────────────┐   ┌───────────────────────┐
 *   │ click bell    │ → │ pick teammate + note    │ → │ POST /assign/ (urgent) │
 *   └──────────────┘   └─────────────────────────┘   └───────────────────────┘
 * DEPENDENCIES:
 *   - ../lib/api (emailApi): GET assignable-members + POST <id>/assign/
 *   - ../../_internal/components/Toast: success / error feedback
 * RELATED:
 *   - EstimatorAllocator.tsx: same /assign/ backend, but gated to quote_request
 *   - organisation/views/email_views/assignment_views.py: the ping endpoint
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { BellRing, Loader2, Mail } from 'lucide-react'
import { emailApi } from '../lib/api'
import { cn } from '../../_internal/lib/utils'
import { useToast } from '../../_internal/components/Toast'

interface AssignableMember {
  user_id: number
  display_name: string
  email: string
  role: string
  /** Free-text job title (e.g. "Estimator"). Drives estimators-first default. */
  position: string
}

/** The urgent prefix is what turns a plain handoff into "raise it as an urgent
 *  notification" — it rides into the SMTP + in-app notification body verbatim
 *  (see assignment_views.py::_send_assignment_email → EmailAssignment.message). */
const URGENT_PREFIX = '🔴 URGENT'
const DEFAULT_NOTE = 'Please action this email as a priority.'
const NOTE_MAX = 480

/** Estimator = job-title position contains "estimat" (case-insensitive). */
function isEstimator(m: AssignableMember): boolean {
  return /estimat/i.test(m.position || '')
}

export function PingTeammateButton({
  emailId,
  onPinged,
  open: openProp,
  onOpenChange,
}: {
  emailId: string
  /** Host refetch so the assignment/visibility re-paints after the ping. */
  onPinged?: () => void
  /** Optional CONTROLLED open state. When provided, the popover's
   *  visibility is owned by the host (e.g. EmailReader's `Shift+@`
   *  shortcut) so the keyboard shortcut and the bell icon share one
   *  popover. Omit for the default self-managed behaviour. */
  open?: boolean
  /** Fires whenever the popover wants to open/close — required when
   *  `open` is controlled. */
  onOpenChange?: (open: boolean) => void
}) {
  const { toast } = useToast()
  const [internalOpen, setInternalOpen] = useState(false)
  // Controlled when the host passes `open`; otherwise self-managed.
  const isControlled = openProp !== undefined
  const open = isControlled ? !!openProp : internalOpen
  const setOpen = useCallback(
    (next: boolean) => {
      if (isControlled) onOpenChange?.(next)
      else setInternalOpen(next)
    },
    [isControlled, onOpenChange],
  )
  const [members, setMembers] = useState<AssignableMember[] | null>(null)
  const [membersLoading, setMembersLoading] = useState(false)
  const [busyUserId, setBusyUserId] = useState<number | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [note, setNote] = useState(DEFAULT_NOTE)
  const rootRef = useRef<HTMLDivElement | null>(null)

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const loadMembers = useCallback(async () => {
    if (members !== null || membersLoading) return
    setMembersLoading(true)
    try {
      const data = await emailApi<{ results: AssignableMember[] }>(
        '/api/emails/assignable-members/',
      )
      setMembers(data?.results ?? [])
    } catch {
      setMembers([])
      toast('Could not load teammates to ping.', 'error')
    } finally {
      setMembersLoading(false)
    }
  }, [members, membersLoading, toast])

  const toggle = useCallback(() => {
    setShowAll(false)
    setOpen(!open)
  }, [open, setOpen])

  // Lazy-load the roster whenever the popover opens — regardless of
  // whether it was opened by the bell icon or a host `Shift+@` shortcut
  // (controlled open). Idempotent: ``loadMembers`` no-ops once loaded.
  useEffect(() => {
    if (open) void loadMembers()
    else setShowAll(false)
  }, [open, loadMembers])

  const ping = useCallback(
    async (member: AssignableMember) => {
      setBusyUserId(member.user_id)
      // Compose the urgent notification text. The prefix is always present so
      // the teammate's email + bell entry read as urgent; the editable note is
      // appended (or the default when blank).
      const body = `${URGENT_PREFIX} - ${(note || DEFAULT_NOTE).trim()}`.slice(
        0,
        NOTE_MAX,
      )
      try {
        const created = await emailApi<{ notification_sent_at: string | null }>(
          `/api/emails/${emailId}/assign/`,
          {
            method: 'POST',
            body: JSON.stringify({
              assignee_user_id: member.user_id,
              message: body,
            }),
          },
        )
        const emailed = !!created?.notification_sent_at
        toast(
          emailed
            ? `Pinged ${member.display_name} (urgent) - emailed + notified.`
            : `Pinged ${member.display_name} (urgent, in-app). Email ping didn't send.`,
          emailed ? 'success' : 'warning',
        )
        setOpen(false)
        onPinged?.()
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Ping failed'
        toast(`Could not ping ${member.display_name}: ${msg}`, 'error')
      } finally {
        setBusyUserId(null)
      }
    },
    [emailId, note, onPinged, toast],
  )

  const all = members ?? []
  const estimators = all.filter(isEstimator)
  const hasEstimators = estimators.length > 0
  const others = all.filter((m) => !isEstimator(m))
  const showingAll = showAll || !hasEstimators
  const visibleMembers = showingAll ? all : estimators

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => toggle()}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Ping a teammate (urgent)"
        aria-label="Ping a teammate as urgent"
        className={cn(
          'p-1.5 rounded-md hover:bg-accent text-rose-600 dark:text-rose-400',
          open && 'bg-accent',
        )}
        data-testid="email-ping-teammate-button"
      >
        <BellRing className="h-4 w-4" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute top-full right-0 z-50 mt-1 w-72 max-h-96 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg py-1"
          data-testid="email-ping-teammate-popover"
        >
          <div className="px-3 pt-1.5 pb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-400">
            <BellRing className="h-3 w-3" aria-hidden /> Ping a teammate · urgent
          </div>

          {/* Editable urgent note carried into the email + in-app notification. */}
          <div className="px-3 pb-2">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={NOTE_MAX}
              rows={2}
              placeholder={DEFAULT_NOTE}
              className="w-full resize-none rounded-md border border-border bg-background px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-rose-400"
              data-testid="email-ping-teammate-note"
            />
          </div>

          {membersLoading ? (
            <div className="flex items-center justify-center gap-2 px-3 py-3 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              <span className="text-[11px]">Loading teammates…</span>
            </div>
          ) : all.length === 0 ? (
            <div className="px-3 py-3 text-[11px] text-muted-foreground">
              No teammates available to ping.
            </div>
          ) : (
            <>
              {hasEstimators && (
                <div className="px-3 pt-1 pb-1.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {showingAll ? 'All teammates' : 'Estimators'}
                </div>
              )}
              {visibleMembers.map((m) => {
                const busy = busyUserId === m.user_id
                return (
                  <button
                    key={m.user_id}
                    type="button"
                    role="option"
                    aria-selected={false}
                    disabled={busyUserId !== null}
                    onClick={() => void ping(m)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/60 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`email-ping-teammate-option-${m.user_id}`}
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-900/40 text-[10px] font-semibold text-rose-700 dark:text-rose-300">
                      {(m.display_name || '?').slice(0, 1).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12px] text-foreground">{m.display_name}</span>
                      <span className="block truncate text-[10px] text-muted-foreground">
                        {m.position || m.email}
                      </span>
                    </span>
                    {busy ? (
                      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" aria-hidden />
                    ) : (
                      <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" aria-hidden />
                    )}
                  </button>
                )
              })}
              {hasEstimators && others.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  className="mt-1 w-full border-t border-border px-3 py-1.5 text-left text-[10px] font-medium text-rose-700 dark:text-rose-300 hover:bg-muted/50"
                >
                  {showingAll
                    ? 'Show estimators only'
                    : `Show all teammates (${all.length})`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default PingTeammateButton
